import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Paper } from '@mui/material';
import {
  Rocket,
  Video,
  Sparkles,
  FileText,
  MessageCircle,
  Youtube,
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionDialog, {
  ActionType,
} from '../features/globalModals/ActionDialog';
import { pushModal } from '../features/globalModals/globalModalsSlice';
import { setSourceFeature, selectPlan } from '../features/plan/planSlice';
import { initializeRecorder } from '../features/recording/recordingSlice';

interface SocialMediaImportDialogProps {
  open: boolean;
  onClose: () => void;
  url: string;
}

const SocialMediaImportDialog: React.FC<SocialMediaImportDialogProps> = ({
  open,
  onClose,
  url,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<{
    teamId: string;
    folderId: string;
  }>();
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [contentTitle, setContentTitle] = useState<string | null>(null);
  const [contentType, setContentType] = useState<'youtube' | null>(null);

  // Get media content info (YouTube only)
  useEffect(() => {
    const getMediaInfo = async () => {
      if (!url) return;

      // YouTube patterns
      const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
        /youtube\.com\/embed\/([^&\n?#]+)/,
      ];

      // Check for YouTube
      const youtubeMatch = youtubePatterns
        .map((pattern) => url.match(pattern))
        .find((match) => match && match[1]);

      if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        setContentType('youtube');
        // Set higher quality thumbnail
        setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`);

        // Get title
        try {
          const response = await fetch(
            `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`
          );
          const data = await response.json();
          setContentTitle(data.title || null);
        } catch (error) {
          console.error('Error fetching YouTube title:', error);
        }
      }
    };

    getMediaInfo();
  }, [url]);

  const handleUpgrade = () => {
    // Set the source feature to youtubeInstagramImports
    dispatch(
      setSourceFeature({
        sourceFeature: 'youtubeInstagramImports',
      })
    );

    // Select the GROWTH plan
    dispatch(
      selectPlan({
        plan: 'GROWTH',
      })
    );

    // Push the upgrade modal
    dispatch(
      pushModal({
        type: 'stripe-pricing-table-upgrade-modal',
        source: 'limits',
      })
    );

    // Close this dialog
    onClose();
  };

  const handleRecord = () => {
    // Initialize screen recorder
    if (teamId && folderId) {
      const settings = {
        audioSettings: { microphoneAudio: true, browserAudio: true },
        videoSettings: { screen: true, webCam: true },
      };
      dispatch(initializeRecorder({ teamId, folderId, settings }));
      onClose();
    }
  };

  const actions: ActionType[] = [
    {
      key: 'record',
      label: t('import.social.actions.recordInstead'),
      onClick: handleRecord,
      variant: 'outlined',
      color: 'secondary',
      startIcon: <Video size={16} />,
      'data-testid': 'import-youtube-record-btn',
    },
    {
      key: 'upgrade',
      label: t('import.social.actions.upgradeNow'),
      onClick: handleUpgrade,
      variant: 'contained',
      color: 'primary',
      startIcon: <Rocket size={16} />,
      'data-testid': 'import-youtube-upgrade-btn',
      sx: { fontWeight: 600, px: 3 },
    },
  ];

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t('import.social.title')}
      actions={actions}
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderRadius: theme.shape.borderRadius,
            bgcolor: theme.palette.background.default,
            position: 'relative',
          }}
        >
          {thumbnailUrl ? (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={
                contentType === 'youtube'
                  ? 'YouTube thumbnail'
                  : 'Instagram thumbnail'
              }
              sx={{
                width: 120,
                height: 68,
                objectFit: 'cover',
                borderRadius: theme.shape.borderRadius,
                mr: 2,
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 120,
                height: 68,
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: theme.shape.borderRadius,
                mr: 2,
                flexShrink: 0,
              }}
            >
              {contentType === 'youtube' ? (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: '#FF0000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Youtube size={30} color="#ffffff" />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: theme.palette.grey[700],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Video size={30} color="#ffffff" />
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              {contentTitle || t('import.social.youtubeVideo')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {url}
            </Typography>
          </Box>

          {/* Ready indicator with minimal text */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                bgcolor: theme.palette.success.main,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: theme.palette.success.main,
                lineHeight: 1,
              }}
            >
              {t('import.social.readyToImport')}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            textAlign: 'center',
            color: theme.palette.text.primary,
            mb: 1.5,
            fontWeight: 500,
            fontSize: '1.1rem',
          }}
        >
          {t('import.social.upgradeMessage')}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            bgcolor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius,
            border: 'none',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                }}
              >
                <Youtube size={14} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('import.social.features.youtube.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('import.social.features.youtube.description')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                }}
              >
                <MessageCircle size={14} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('import.social.features.transcription.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('import.social.features.transcription.description')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                }}
              >
                <Sparkles size={14} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('import.social.features.ai.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('import.social.features.ai.description')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                }}
              >
                <FileText size={14} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {t('import.social.features.documents.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('import.social.features.documents.description')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ActionDialog>
  );
};

export default SocialMediaImportDialog;
